import {ConfirmationDialog, dateTime, DetailPane} from '@management-ui/core';
import {Button, styled} from '@mui/material';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {ServiceContext} from '../../../../components/Services';
import withOrderStatuses from '../../../../components/withOrderStatuses';
import {usePermissions} from '../../../../hooks';

const Actions = styled('div')(({theme}) => ({
  display: 'flex',
  margin: theme.spacing(-1, 0, 0),

  '& .MuiButton-root': {
    color: theme.palette.common.white,
    margin: theme.spacing(0.5, 1, 0.5, 0)
  }
}));

const Admin = ({list, onListUpdated, onLoading, statuses}) => {
  const services = useContext(ServiceContext);
  const {userCan} = usePermissions();
  const [canUnlock, setCanUnlock] = useState(false);
  const [actions, setActions] = useState([]);
  const [showLock, setShowLock] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);

  useEffect(() => {
    setCanUnlock(userCan(['unlock_orders'], true, false));
  }, [userCan]);

  const handleLock = useCallback((confirmed) => {
    setShowLock(false);
    if (confirmed) {
      onLoading(true);
      services.packingList.lockList(list).then((locked) => {
        onLoading(false);
        onListUpdated(locked);
      }).catch(() => onLoading(false));
    }
  }, [list, onListUpdated, onLoading, services]);

  const handleUnlock = useCallback((confirmed) => {
    setShowUnlock(false);
    if (confirmed) {
      onLoading(true);
      services.packingList.unlockList(list).then((unlocked) => {
        onLoading(false);
        onListUpdated(unlocked);
      }).catch(() => onLoading(false));
    }
  }, [list, onListUpdated, onLoading, services]);

  const handleDownload = useCallback(() => {
    onLoading(true);
    services.packingList.downloadList(list)
      .then(() => onLoading(false))
      .catch(() => onLoading(false));
  }, [services, list, onLoading]);

  useEffect(() => {
    const getColour = (status) => statuses.find(({key}) => key === status)?.colour ?? 'red';

    const actions = [];
    if (!list.archived) {
      if (list.locked) {
        if (canUnlock) {
          actions.push({title: 'Unlock List', colour: getColour('pending'), onClick: () => setShowUnlock(true)});
        };
      } else {
        actions.push(
          {title: 'Lock List', colour: getColour('locked'), onClick: () => setShowLock(true)}
        );
      }
    }
    if (list.locked) {
      actions.push(
        {title: 'Download', colour: getColour('locked'), onClick: handleDownload}
      );
    }
    setActions(actions);
  }, [list, statuses, canUnlock, handleDownload]);

  return (
    <DetailPane
      title="Admin Details"
      details={useMemo(() => [
        {title: 'Created', value: dateTime(list.created_at)},
        {title: 'Last Updated', value: dateTime(list.updated_at)},
        ...(list.locked ? [{title: 'Locked', value: dateTime(list.locked_at)}] : []),
        ...(actions.length > 0 ? [{
          title: 'Actions', value: (
            <Actions>
              {actions.map(({title, colour, onClick, disabled = false}, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="contained"
                  style={disabled ? {} : {backgroundColor: colour}}
                  onClick={onClick}
                  disabled={disabled}
                >
                  {title}
                </Button>
              ))}
            </Actions>
          )
        }] : [])
      ], [list, actions])}
      dialogs={[
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showLock}
            title="Lock Packing List"
            message="Are you sure you want to lock this list?"
            onClose={handleLock}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showUnlock}
            title="Unlock Packing List"
            message="Are you sure you want to unlock this list?"
            onClose={handleUnlock}
          />
        )
      ]}
    />
  );
};

export default withOrderStatuses(Admin);
